import React, { useState } from "react";
import Box from "@mui/material/Box";
import { IconButton, Typography } from "@mui/material";
import Card from "@mui/material/Card";
import { ExpandMore, ExpandLess } from "components/icons";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import styles from "./CheckpointCard.module.css";
import CheckpointStatusResponse from "./responseTypes/CheckpointStatusResponse";
import CheckpointTimeResponse from "./responseTypes/CheckpointTimeResponse";
import CheckpointDateTimeResponse from "./responseTypes/CheckpointDateTimeResponse";
import CheckpointCommentResponse from "./responseTypes/CheckpointCommentResponse";
import CheckpointDropdownResponse from "./responseTypes/CheckpointDropdownResponse";
import CheckpointAttachmentResponse from "./responseTypes/CheckpointAttachmentResponse";
import { useActivePatrolData } from "../../context/activePatrol/ActivePatrolDataProvider";
import { getAutoFillDropdownItems } from "../../utils/getAutoFillDropdownItems";
interface CheckpointCardProps {
  index: number;
  checkpoint: PatrolCheckpoint | undefined;
}
const CheckpointCard = (props: CheckpointCardProps) => {
  const { checkpoint } = props;
  const { handleCheckpointInput } = useActivePatrolData();
  const [showContent, setShowContent] = useState<boolean>(false);

  const requireComment = checkpoint?.Status.find(
    (status) => status.StatusID === checkpoint.SelectedStatusID
  )?.isCommentRequired;

  const autoFillDropdownItems = checkpoint
    ? getAutoFillDropdownItems(checkpoint)
    : ([] as string[]);

  const onInput = (type: CheckpointInputTypes, newValue: any) => {
    if (!checkpoint) return;
    handleCheckpointInput(
      checkpoint.ChecklistID,
      checkpoint.CheckpointID,
      type,
      newValue
    );
  };

  const getCompletionColor = (
    completionStatus: CheckpointCompletionStatus | undefined
  ) => {
    switch (completionStatus) {
      case "Info":
        return "info";
      case "Clear":
        return "success";
      case "Warning":
        return "warning";
      case "Emergency":
        return "error";
      default:
        return "info";
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {checkpoint?.CompletionStatus !== "Incomplete" && (
        <RadioButtonCheckedIcon
          className={styles.completionIcon}
          color={getCompletionColor(checkpoint?.CompletionStatus)}
        />
      )}
      <Card variant="outlined">
        <Box sx={{ padding: "0.5rem" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            onClick={() => setShowContent((prev) => !prev)}
          >
            <Typography variant="body1">
              {props.index + 1}. {props.checkpoint?.Name}
            </Typography>
            <IconButton>
              {showContent ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          {showContent && (
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1.5 }}
                color="text.secondary"
              >
                {props.checkpoint?.Description}
              </Typography>
              {checkpoint?.ResponseTypeName === "Status" && (
                <CheckpointStatusResponse
                  statuses={checkpoint.Status}
                  onStatusClick={(selectedStatusID: number) =>
                    onInput("SelectedStatusID", selectedStatusID)
                  }
                  selectedStatusID={checkpoint.SelectedStatusID}
                />
              )}
              {(checkpoint?.ResponseTypeName === "Comment" ||
                requireComment) && (
                <CheckpointCommentResponse
                  value={checkpoint?.Comment || ""}
                  error={false}
                  helperText=""
                  label={
                    requireComment
                      ? "Please provide additional comments"
                      : "Enter Text"
                  }
                  onChange={(e) => onInput("Comment", e.target.value)}
                  autoFillDropdownItems={autoFillDropdownItems}
                />
              )}
              {checkpoint?.ResponseTypeName === "Time" && (
                <CheckpointTimeResponse
                  startTime={checkpoint.Time ? new Date(checkpoint.Time) : null}
                  onUpdateTime={(newValue) => onInput("Time", newValue)}
                />
              )}

              {checkpoint?.ResponseTypeName === "DateTime" && (
                <CheckpointDateTimeResponse
                  value={checkpoint.DateTime}
                  onChange={(val) => onInput("DateTime", val)}
                />
              )}

              {(checkpoint?.ResponseTypeName === "Dropdown" ||
                checkpoint?.ResponseTypeName === "DropdownMultiSelect") && (
                <CheckpointDropdownResponse
                  dropdownItems={checkpoint.Dropdowns || []}
                  selectedIDs={checkpoint.SelectedDropdownIDs || []}
                  allowMultiSelect={
                    checkpoint.ResponseTypeName === "DropdownMultiSelect"
                  }
                  setSelectedDropdownIDs={(selectedIDs: number[]) =>
                    onInput("SelectedDropdownIDs", selectedIDs)
                  }
                />
              )}
              {(checkpoint?.ResponseTypeName === "Signature" ||
                checkpoint?.ResponseTypeName === "Attachment") && (
                <CheckpointAttachmentResponse
                  uppy={checkpoint.UppyInstance}
                  checkpointID={checkpoint.CheckpointID}
                  attachments={checkpoint.Attachments || []}
                  onUpdateAttachments={(attachments: PatrolAttachment[]) =>
                    onInput("Attachments", attachments)
                  }
                  isTypeSignature={checkpoint.ResponseTypeName === "Signature"}
                />
              )}
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};
export default CheckpointCard;
